import { RecordShareBaseParams } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { AxiosError, isAxiosError } from 'axios';
import isString from 'lodash/isString';
import { parseMessageFromError } from '../api/axios';

/** `status` when the request never received a response (network failure, abort, CORS). */
export const STATUS_NO_RESPONSE = 'no-response';
/** `status` when the failure did not come from an HTTP request at all (parse error, thrown Error). */
export const STATUS_NOT_HTTP = 'not-http';

// Both are declared as type aliases rather than interfaces so they stay
// assignable to the `Record<string, unknown>` attribute bag Sentry's logger
// takes — an interface is not.

/**
 * Identifying attributes for a record-share (quote/invoice) session.
 *
 * The share token is included deliberately: it is the only value support can
 * use to reproduce a customer's broken link, and `tokenLength` makes a
 * truncated link obvious at a glance — a healthy token is a 36-char UUID, so
 * anything shorter means the link was mangled in transit.
 */
export type RecordShareLogContext = {
  recordId: string;
  tenantId: string;
  token: string;
  tokenLength: number;
};

export type RecordShareErrorLogContext = RecordShareLogContext & {
  /** HTTP status as a string, or `no-response` / `not-http`. */
  status: string;
  /** Axios error code (`ERR_NETWORK`, `ERR_CANCELED`, `ECONNABORTED`, …) — separates aborts from real network errors. */
  errorCode: string;
  errorName: string;
  errorMessage: string;
  /** Message parsed out of the response body, when the server sent one. */
  responseMessage: string;
};

export function getRecordShareLogContext({
  recordId,
  tenantId,
  token,
}: Partial<RecordShareBaseParams>): RecordShareLogContext {
  return {
    recordId: recordId || '',
    tenantId: tenantId || '',
    token: token || '',
    tokenLength: token?.length ?? 0,
  };
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return { errorName: error.name, errorMessage: error.message };
  }
  // A rejection can also be nothing at all (Stripe's onLoadError fires with an
  // optional error), and String(undefined) reads like a real message in the log.
  if (error == null) {
    return { errorName: 'unknown', errorMessage: '' };
  }
  // Not every rejection is an Error — Stripe, for one, rejects with a plain
  // object, and String()-ing that yields a useless "[object Object]".
  const { name, message } = error as {
    name?: unknown;
    message?: unknown;
  };
  return {
    errorName: isString(name) ? name : 'unknown',
    errorMessage: isString(message) ? message : String(error),
  };
}

function getResponseMessage(error: AxiosError) {
  // Only worth parsing when the server actually sent a body — otherwise
  // parseMessageFromError returns its generic user-facing fallback, which
  // would look like a server message in the log without being one.
  if (!error.response?.data) {
    return '';
  }
  try {
    // parseMessageFromError decodes ArrayBuffer bodies, which is what the PDF
    // request (`responseType: 'arraybuffer'`) returns on failure.
    const { detail, summary } = parseMessageFromError(error);
    return detail || summary || '';
  } catch {
    return '';
  }
}

/**
 * Builds the attribute bag for a failed record-share request. Every value is a
 * primitive so it can be passed straight to `Sentry.logger.*`.
 */
export function getRecordShareErrorLogContext(
  authParams: Partial<RecordShareBaseParams>,
  error: unknown,
): RecordShareErrorLogContext {
  const { errorName, errorMessage } = getErrorMessage(error);

  if (!isAxiosError(error)) {
    return {
      ...getRecordShareLogContext(authParams),
      status: STATUS_NOT_HTTP,
      errorCode: 'unknown',
      errorName,
      errorMessage,
      responseMessage: '',
    };
  }

  return {
    ...getRecordShareLogContext(authParams),
    status: error.response?.status
      ? String(error.response.status)
      : STATUS_NO_RESPONSE,
    errorCode: error.code || 'unknown',
    errorName,
    errorMessage,
    responseMessage: getResponseMessage(error),
  };
}

/**
 * Sentry scope for `captureException` on a record-share failure. Ids and the
 * token go on tags so they are searchable in Issues, and the whole context is
 * repeated under `contexts.recordShare` for readability on the event page.
 */
export function getRecordShareCaptureContext(
  context: RecordShareErrorLogContext,
  tags: Record<string, Maybe<string>> = {},
) {
  return {
    tags: {
      type: 'RECORD_SHARE',
      status: context.status,
      errorCode: context.errorCode,
      tenantId: context.tenantId,
      recordId: context.recordId,
      shareToken: context.token,
      ...tags,
    },
    contexts: { recordShare: { ...context } },
  };
}
